import { reactive, computed } from 'vue'

export interface PageAction {
  key: string
  label: string
}

export interface PageConfig {
  key: string
  label: string
  exclusive?: boolean
  actions: PageAction[]
}

export interface Profile {
  id: string
  name: string
  color: string
  teamIds: number[]
  userIds: number[]
  permissions: Record<string, boolean>
}

export interface AdminConfig {
  profiles: Profile[]
  globalSettings: {
    generateProtocol: boolean
    betoAiEnabled: boolean
    resolveWithMacroEnabled: boolean
    resolveWithMacroId: string
    autoLogoutMinutes: number
    restrictedTemplateSendingEnabled: boolean
    broadcastEnabled: boolean
    toleranceNotificationsEnabled: boolean
  }
}

export const PAGES_CONFIG: PageConfig[] = [
  {
    key: 'geral',
    label: 'Regra Geral',
    exclusive: true,
    actions: [
      { key: 'visaoSomenteAgente', label: 'Somente atribuições do Agente' },
      { key: 'visaoAgenteETime',   label: 'Atribuições do Agente e do(s) Time(s)' },
      { key: 'visaoTodos',         label: 'Todas as Atribuições' },
    ],
  },
  {
    key: 'dashboard',
    label: 'Dashboard',
    actions: [
      { key: 'view',       label: 'Visualizar' },
      { key: 'viewKpis',   label: 'Ver KPIs' },
      { key: 'viewCharts', label: 'Ver Gráficos' },
      { key: 'viewAgents', label: 'Ver Agentes' },
      { key: 'viewTeams',  label: 'Ver Times' },
      { key: 'hideFluxo',  label: 'Esconder Fluxo' },
    ],
  },
  {
    key: 'foco',
    label: 'Modo Foco',
    actions: [
      { key: 'view', label: 'Visualizar' },
    ],
  },
  {
    key: 'templates',
    label: 'Templates WA',
    actions: [
      { key: 'view',   label: 'Visualizar' },
      { key: 'create', label: 'Criar' },
      { key: 'edit',   label: 'Editar' },
      { key: 'delete', label: 'Excluir' },
    ],
  },
  {
    key: 'resumo',
    label: 'Resumo',
    actions: [
      { key: 'view', label: 'Visualizar' },
    ],
  },
  {
    key: 'relatorioResolucao',
    label: 'Relatório de Resolução',
    actions: [
      { key: 'view', label: 'Visualizar' },
    ],
  },
  {
    key: 'metricas',
    label: 'Métricas',
    actions: [
      { key: 'view', label: 'Visualizar' },
    ],
  },
  {
    key: 'contacts',
    label: 'Contatos',
    actions: [
      { key: 'view',   label: 'Visualizar' },
      { key: 'export', label: 'Exportar' },
    ],
  },
  {
    key: 'sla',
    label: 'SLA',
    actions: [
      { key: 'view',      label: 'Visualizar' },
      { key: 'configure', label: 'Configurar' },
    ],
  },
  {
    key: 'teams',
    label: 'Times',
    actions: [
      { key: 'view',          label: 'Visualizar' },
      { key: 'create',        label: 'Criar Time' },
      { key: 'edit',          label: 'Editar Time' },
      { key: 'delete',        label: 'Excluir Time' },
      { key: 'manageMembers', label: 'Gerenciar Membros' },
    ],
  },
  {
    key: 'macros',
    label: 'Macros',
    actions: [
      { key: 'view',   label: 'Visualizar' },
      { key: 'create', label: 'Criar' },
      { key: 'edit',   label: 'Editar' },
      { key: 'delete', label: 'Excluir' },
    ],
  },
  {
    key: 'etiquetas',
    label: 'Etiquetas',
    actions: [
      { key: 'view',   label: 'Visualizar' },
      { key: 'create', label: 'Criar' },
      { key: 'edit',   label: 'Editar' },
      { key: 'delete', label: 'Excluir' },
    ],
  },
  {
    key: 'avaliacoes',
    label: 'Avaliações',
    actions: [
      { key: 'view', label: 'Visualizar' },
    ],
  },
  {
    key: 'agentes',
    label: 'Agentes',
    actions: [
      { key: 'view',   label: 'Visualizar' },
      { key: 'delete', label: 'Excluir Agente' },
    ],
  },
  {
    key: 'fila',
    label: 'Fila de Chamados',
    actions: [
      { key: 'view',      label: 'Visualizar' },
      { key: 'configure', label: 'Configurar Capacidade' },
    ],
  },
  {
    key: 'slaMetro',
    label: 'SLA Telão',
    actions: [
      { key: 'view', label: 'Visualizar' },
    ],
  },
  {
    key: 'messages',
    label: 'Mensagens',
    actions: [
      { key: 'generateProtocol', label: 'Gerar Protocolo' },
      { key: 'send',             label: 'Enviar Mensagens' },
      { key: 'sendTemplate',     label: 'Enviar Templates' },
      { key: 'viewHistory',      label: 'Ver Histórico' },
      { key: 'setPriority',      label: 'Setar Prioridade' },
      { key: 'snooze',        label: 'Adiar (Snooze)' },
      { key: 'assignAgent',   label: 'Atribuir Agentes' },
      { key: 'assignTeam',    label: 'Atribuir Time' },
      { key: 'changeStatus',  label: 'Resolver / Pendente' },
      { key: 'runMacro',      label: 'Executar Macro' },
      { key: 'privateNote',   label: 'Enviar Nota Interna' },
    ],
  },
  {
    key: 'agentChat',
    label: 'Chat entre Agentes',
    actions: [
      { key: 'view', label: 'Visualizar / Usar' },
      { key: 'card', label: 'Chat pelo Card do Agente (Dashboard)' },
    ],
  },
  {
    key: 'broadcast',
    label: 'Aviso a Agentes',
    actions: [
      { key: 'send',        label: 'Enviar Aviso (tecla K)' },
      { key: 'viewHistory', label: 'Ver Histórico (Admin)' },
    ],
  },
  {
    key: 'blacklist',
    label: 'Lista Negra',
    actions: [
      { key: 'manage', label: 'Bloquear, desbloquear e ver a aba Bloqueados' },
    ],
  },
  {
    key: 'admin',
    label: 'Administração',
    actions: [
      { key: 'view',          label: 'Visualizar' },
      { key: 'editProfile',   label: 'Editar Perfil' },
      { key: 'createProfile', label: 'Criar Perfil' },
      { key: 'createTeam',    label: 'Criar Time' },
      { key: 'createAgent',   label: 'Criar Agente' },
    ],
  },
  {
    key: 'notificacoesAgente',
    label: 'Notificação por WhatsApp ou E-mail',
    actions: [
      { key: 'view', label: 'Ver seção no próprio perfil' },
    ],
  },
  {
    key: 'notificacoesConfig',
    label: 'Notificações & Templates (Admin)',
    actions: [
      { key: 'view', label: 'Visualizar' },
    ],
  },
  {
    key: 'ai',
    label: 'Agente de IA e R.A.G',
    actions: [
      { key: 'view', label: 'Visualizar' },
      { key: 'edit', label: 'Criar/editar agentes e documentos' },
      { key: 'mark', label: 'Marcar conversas pra base (só perfil Sistema)' },
    ],
  },
]

export const PROTECTED_PROFILE_IDS = ['admin', 'supervisor', 'agent']

// Nomes reservados de perfil de sistema — protegidos contra exclusão por nome,
// não por id, porque perfis como "Sistema" são criados via UI (id dinâmico,
// gerado em runtime), diferente de admin/supervisor/agent (id fixo no código).
// A ordem da lista também define a ordem de exibição dos perfis (mais importante primeiro).
export const RESERVED_PROFILE_NAMES = ['Sistema', 'Administrador', 'Supervisor', 'Agente', 'EC - ISBET']

export function isProtectedProfile(profile: Profile): boolean {
  return PROTECTED_PROFILE_IDS.includes(profile.id) || RESERVED_PROFILE_NAMES.includes(profile.name)
}

function profileSortIndex(profile: Profile): number {
  const idx = RESERVED_PROFILE_NAMES.indexOf(profile.name)
  return idx === -1 ? RESERVED_PROFILE_NAMES.length : idx
}

const STORAGE_KEY = 'admin_config'

// Fallback padrão de "Resolver com Macro" — usado antes do valor do MySQL carregar
// (ou se a linha ainda não existir em admin_settings). Ver migrations/admin-mysql-migration-v8.sql.
export const DEFAULT_RESOLVE_MACRO_ID = '4b574e22-e29e-4f1b-9292-e29953866f73'

function allPerms(value: boolean): Record<string, boolean> {
  return Object.fromEntries(
    PAGES_CONFIG.flatMap(p => p.actions.map(a => [`${p.key}.${a.key}`, value]))
  )
}

const DEFAULT_PROFILES: Profile[] = [
  {
    id: 'admin',
    name: 'Administrador',
    color: '#BC0C24',
    teamIds: [],
    userIds: [],
    permissions: {
      ...allPerms(true),
      'geral.visaoSomenteAgente': false,
      'geral.visaoAgenteETime':   false,
      'geral.visaoTodos':         true,
      // Chat pelo card do agente no Dashboard: desabilitado por padrão pra
      // todo mundo, só o perfil "Sistema" recebe (ver migrations/admin-mysql-migration-v29.sql).
      'agentChat.card':           false,
    },
  },
  {
    id: 'supervisor',
    name: 'Supervisor',
    color: '#1F8C5A',
    teamIds: [],
    userIds: [],
    permissions: {
      ...allPerms(true),
      'geral.visaoSomenteAgente': false,
      'geral.visaoAgenteETime':   true,
      'geral.visaoTodos':         false,
      'templates.delete': false,
      'sla.configure':    false,
      'teams.delete':     false,
      'agentes.delete':   false,
      'admin.view':          false,
      'admin.editProfile':   false,
      'admin.createProfile': false,
      'admin.createTeam':    false,
      'admin.createAgent':   false,
      'agentChat.card':      false,
    },
  },
  {
    id: 'agent',
    name: 'Agente',
    color: '#0369A1',
    teamIds: [],
    userIds: [],
    permissions: {
      'geral.visaoSomenteAgente': true,
      'geral.visaoAgenteETime':   false,
      'geral.visaoTodos':         false,
      'dashboard.view':              true,
      'dashboard.viewKpis':          true,
      'dashboard.viewCharts':        false,
      'dashboard.viewAgents':        false,
      'dashboard.viewTeams':         false,
      'foco.view':                   true,
      'templates.view':              true,
      'templates.create':            false,
      'templates.edit':              false,
      'templates.delete':            false,
      'resumo.view':                 true,
      'contacts.view':               true,
      'contacts.export':             false,
      'sla.view':                    true,
      'sla.configure':               false,
      'teams.view':          false,
      'teams.create':        false,
      'teams.edit':          false,
      'teams.delete':        false,
      'teams.manageMembers': false,
      'macros.view':         true,
      'macros.create':       true,
      'macros.edit':         true,
      'macros.delete':       false,
      'etiquetas.view':      false,
      'etiquetas.create':    false,
      'etiquetas.edit':      false,
      'etiquetas.delete':    false,
      'avaliacoes.view':     false,
      'agentes.view':        false,
      'agentes.delete':      false,
      'fila.view':           false,
      'fila.configure':      false,
      'slaMetro.view':       true,
      'agentChat.view':      true,
      'agentChat.card':      false,
      'messages.generateProtocol':   true,
      'messages.send':               true,
      'messages.sendTemplate':       true,
      'messages.viewHistory':        true,
      'messages.setPriority':        false,
      'messages.snooze':             true,
      'messages.assignAgent':        false,
      'messages.assignTeam':         false,
      'messages.changeStatus':       true,
      'messages.runMacro':           true,
      'messages.privateNote':        false,
      'admin.view':          false,
      'admin.editProfile':   false,
      'admin.createProfile': false,
      'admin.createTeam':    false,
      'admin.createAgent':   false,
    },
  },
]

function allPermKeys(): string[] {
  return PAGES_CONFIG.flatMap(p => p.actions.map(a => `${p.key}.${a.key}`))
}

function defaultPermForProfile(id: string): Record<string, boolean> {
  const def = DEFAULT_PROFILES.find(p => p.id === id)
  return def ? { ...def.permissions } : allPerms(false)
}

function ensureProfileIntegrity(profiles: Profile[]): Profile[] {
  // 1. Ensure protected profiles always exist
  for (const def of DEFAULT_PROFILES.filter(d => PROTECTED_PROFILE_IDS.includes(d.id))) {
    if (!profiles.find(p => p.id === def.id)) {
      profiles.unshift({ ...def, userIds: [], teamIds: [] })
    }
  }

  // 2. Fill/enforce permission keys using default profile as reference
  const keys = allPermKeys()
  for (const p of profiles) {
    if (!p.permissions || typeof p.permissions !== 'object') {
      p.permissions = defaultPermForProfile(p.id)
      continue
    }
    const fallback = defaultPermForProfile(p.id)
    // Preenche só as chaves AUSENTES (permissão nova que esse perfil ainda não
    // tinha salva) — nunca sobrescreve um valor já existente, mesmo pro admin.
    // Antes o admin tinha TODAS as chaves forçadas pro default a cada load, o
    // que impedia qualquer restrição salva no banco de ter efeito (a permissão
    // sempre voltava a true). Preenchimento condicional resolve os dois casos:
    // permissão nova continua sendo concedida automaticamente ao admin (a chave
    // não existe ainda no objeto salvo), e uma restrição explícita (chave já
    // presente, valor false) passa a ser respeitada.
    for (const key of keys) {
      if (!(key in p.permissions)) {
        p.permissions[key] = fallback[key] ?? false
      }
    }
  }

  return profiles
}

function loadConfig(): AdminConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<AdminConfig>
      const profiles = ensureProfileIntegrity(
        Array.isArray(parsed.profiles) ? parsed.profiles : []
      )
      return {
        profiles,
        globalSettings: {
          generateProtocol:        parsed.globalSettings?.generateProtocol ?? true,
          betoAiEnabled:           parsed.globalSettings?.betoAiEnabled ?? false,
          resolveWithMacroEnabled: parsed.globalSettings?.resolveWithMacroEnabled ?? true,
          resolveWithMacroId:      parsed.globalSettings?.resolveWithMacroId ?? DEFAULT_RESOLVE_MACRO_ID,
          autoLogoutMinutes:       Number(parsed.globalSettings?.autoLogoutMinutes ?? 0),
          restrictedTemplateSendingEnabled: parsed.globalSettings?.restrictedTemplateSendingEnabled ?? false,
          broadcastEnabled:        parsed.globalSettings?.broadcastEnabled ?? true,
          toleranceNotificationsEnabled: parsed.globalSettings?.toleranceNotificationsEnabled ?? true,
        },
      }
    }
  } catch { /* ignore corrupt storage */ }
  return {
    profiles: DEFAULT_PROFILES.map(p => ({ ...p })),
    globalSettings: {
      generateProtocol: true, betoAiEnabled: false,
      resolveWithMacroEnabled: true, resolveWithMacroId: DEFAULT_RESOLVE_MACRO_ID,
      autoLogoutMinutes: 0,
      restrictedTemplateSendingEnabled: false,
      broadcastEnabled: true,
      toleranceNotificationsEnabled: true,
    },
  }
}

function persist(config: AdminConfig) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config))
}

const state = reactive<AdminConfig>(loadConfig())

// Sincroniza perfis/permissões entre abas: o evento 'storage' só dispara nas
// abas que NÃO fizeram a escrita, então um admin editando em uma aba atualiza
// as demais (ex.: agente com o dashboard aberto) sem precisar de reload.
window.addEventListener('storage', (e) => {
  if (e.key !== STORAGE_KEY || !e.newValue) return
  try {
    const parsed = JSON.parse(e.newValue) as Partial<AdminConfig>
    const profiles = ensureProfileIntegrity(Array.isArray(parsed.profiles) ? parsed.profiles : [])
    state.profiles.splice(0, state.profiles.length, ...profiles)
    state.globalSettings.generateProtocol        = parsed.globalSettings?.generateProtocol ?? true
    state.globalSettings.betoAiEnabled           = parsed.globalSettings?.betoAiEnabled ?? false
    state.globalSettings.resolveWithMacroEnabled = parsed.globalSettings?.resolveWithMacroEnabled ?? true
    state.globalSettings.resolveWithMacroId      = parsed.globalSettings?.resolveWithMacroId ?? DEFAULT_RESOLVE_MACRO_ID
    state.globalSettings.autoLogoutMinutes       = Number(parsed.globalSettings?.autoLogoutMinutes ?? 0)
    state.globalSettings.restrictedTemplateSendingEnabled = parsed.globalSettings?.restrictedTemplateSendingEnabled ?? false
    state.globalSettings.broadcastEnabled        = parsed.globalSettings?.broadcastEnabled ?? true
    state.globalSettings.toleranceNotificationsEnabled = parsed.globalSettings?.toleranceNotificationsEnabled ?? true
  } catch { /* ignore corrupt storage */ }
})

/**
 * Replace in-memory profiles with data loaded from the database.
 * Preserves integrity (protected profiles, missing permission keys).
 * Called on startup (if session restored) and after login.
 */
export function applyDBProfiles(dbProfiles: Array<{
  id: string
  name: string
  color: string
  team_ids: number[]
  user_ids: number[]
  permissions: Record<string, boolean>
}>): void {
  if (!dbProfiles.length) return
  const mapped: Profile[] = dbProfiles.map(d => ({
    id:          d.id,
    name:        d.name,
    color:       d.color,
    teamIds:     d.team_ids,
    userIds:     d.user_ids,
    permissions: d.permissions,
  }))
  const integrated = ensureProfileIntegrity(mapped)

  // Deduplicate: if a user appears in multiple profiles (DB inconsistency),
  // keep them only in the highest-priority one. Custom profiles win over built-ins.
  const PROFILE_PRIORITY: Record<string, number> = { admin: 1, supervisor: 2, agent: 3 }
  const byPriority = [...integrated].sort((a, b) => {
    const pa = PROFILE_PRIORITY[a.id] ?? 10
    const pb = PROFILE_PRIORITY[b.id] ?? 10
    return pb - pa // descending — highest priority processed first
  })
  const claimed = new Set<number>()
  for (const p of byPriority) {
    p.userIds = p.userIds.filter(uid => !claimed.has(uid))
    p.userIds.forEach(uid => claimed.add(uid))
  }

  state.profiles.splice(0, state.profiles.length, ...integrated)
  persist(state)
}

/**
 * Auto-assign the logged-in user to their role-based default profile.
 * Skipped if the user is already in ANY profile (the dashboard assignment
 * always takes priority over the Chatwoot role).
 * Called on login and on session restore.
 */
export function syncUserRole(userId: number, role: string): void {
  const inAnyProfile = state.profiles.some(p => p.userIds.includes(userId))
  if (inAnyProfile) return

  const targetId = role === 'administrator' ? 'admin' : 'agent'
  const target   = state.profiles.find(p => p.id === targetId)
  if (target && !target.userIds.includes(userId)) {
    target.userIds.push(userId)
    persist(state)
  }
}

/**
 * Bulk-sync an array of Chatwoot users to their role-based default profiles.
 * Called when the admin loads ProfilesSection and the full agents list is fetched.
 * Users already in ANY profile (custom or default) are left untouched — the
 * dashboard assignment always takes priority over the Chatwoot role.
 */
export function bulkSyncRoles(users: Array<{ id: number; role: string }>): void {
  let changed = false
  for (const u of users) {
    const inAnyProfile = state.profiles.some(p => p.userIds.includes(u.id))
    if (inAnyProfile) continue

    const targetId = u.role === 'administrator' ? 'admin' : 'agent'
    const target   = state.profiles.find(p => p.id === targetId)
    if (target && !target.userIds.includes(u.id)) {
      target.userIds.push(u.id)
      changed = true
    }
  }
  if (changed) persist(state)
}

/**
 * Returns the ISBET profile name for a given Chatwoot user ID.
 * Falls back to the Chatwoot role label if the user is not in any profile.
 */
/** Perfis efetivos de um usuário — atribuição por userId tem prioridade exclusiva
 *  sobre atribuição por time (ignora teamIds se o usuário já está explícito em algum perfil). */
export function resolveUserProfiles(userId: number, userTeamIds: number[] = []): Profile[] {
  const byUserId = state.profiles.filter(p => p.userIds.includes(userId))
  if (byUserId.length) return byUserId
  if (!userTeamIds.length) return []
  return state.profiles.filter(p => p.teamIds.some(t => userTeamIds.includes(t)))
}

/** Resolve o perfil do wootdash de um usuário — nunca cai pro role do Chatwoot. */
export function resolveProfile(userId: number, teamIds: number[] = []): Profile | null {
  return resolveUserProfiles(userId, teamIds)[0] ?? null
}

export function profileLabel(userId: number, teamIds: number[] = []): string {
  return resolveProfile(userId, teamIds)?.name ?? 'Sem perfil'
}

// Perfis com visão global travada pelo NOME — mesmo que a Regra Geral (abaixo) esteja
// desmarcada ou mal configurada, Administrador e EC - ISBET sempre veem/atuam em
// qualquer conversa/time, independente de serem membros reais no Chatwoot. Diferente
// dos outros perfis, isso não depende de nenhuma configuração — é garantido pelo nome.
export const UNRESTRICTED_TEAM_PROFILE_NAMES = ['Administrador', 'EC - ISBET']

export interface RoleScope {
  hasSomenteAgente: boolean
  hasAgenteETime: boolean
  hasTodos: boolean
  /** Administrador / EC - ISBET — ignora tudo abaixo, vê e atua em qualquer time. */
  unrestricted: boolean
  /** Times reais do Chatwoot (userTeamIds) — nunca inclui profile.teamIds (ver roadmap). */
  tids: number[]
}

/**
 * Escopo de visibilidade por time, centralizado pra não duplicar a lógica da Regra
 * Geral (geral.visaoSomenteAgente / visaoAgenteETime / visaoTodos) em cada tela —
 * usado por DashboardView.vue, useMyConversations.ts, MobileView.vue, AvaliacoesView.vue
 * e AgentesView.vue. `tids` é só o time real do Chatwoot: o campo "Times" configurado
 * no perfil NUNCA amplia esse escopo — só serve pra herança automática de perfil
 * (resolveUserProfiles), não pra decidir o que o usuário pode ver.
 */
export function resolveRoleScope(userId: number, userTeamIds: number[] = []): RoleScope {
  const userProfiles = resolveUserProfiles(userId, userTeamIds)
  return {
    hasSomenteAgente: userProfiles.some(p => p.permissions['geral.visaoSomenteAgente'] === true),
    hasAgenteETime:   userProfiles.some(p => p.permissions['geral.visaoAgenteETime']   === true),
    hasTodos:         userProfiles.some(p => p.permissions['geral.visaoTodos']         === true),
    unrestricted:     userProfiles.some(p => UNRESTRICTED_TEAM_PROFILE_NAMES.includes(p.name)),
    tids: userTeamIds,
  }
}

export function useAdmin() {
  function save() { persist(state) }

  function addProfile(data: Omit<Profile, 'id'>): Profile {
    const p: Profile = { ...data, id: Date.now().toString(36) }
    state.profiles.push(p)
    save()
    return p
  }

  function updateProfile(id: string, data: Partial<Omit<Profile, 'id'>>): string[] {
    const idx = state.profiles.findIndex(p => p.id === id)
    if (idx === -1) return []
    const affectedIds: string[] = [id]
    // Exclusivity: users newly added to this profile are removed from all others
    if (data.userIds !== undefined) {
      const prev  = state.profiles[idx].userIds
      const added = data.userIds.filter(uid => !prev.includes(uid))
      if (added.length) {
        state.profiles.forEach((p, i) => {
          if (i === idx) return
          const before = p.userIds.length
          p.userIds = p.userIds.filter(uid => !added.includes(uid))
          if (p.userIds.length !== before) affectedIds.push(p.id)
        })
      }
    }
    Object.assign(state.profiles[idx], data)
    save()
    return affectedIds
  }

  function deleteProfile(id: string) {
    const profile = state.profiles.find(p => p.id === id)
    if (!profile || isProtectedProfile(profile)) return
    const idx = state.profiles.findIndex(p => p.id === id)
    if (idx !== -1) { state.profiles.splice(idx, 1); save() }
  }

  function togglePermission(profileId: string, permKey: string) {
    const profile = state.profiles.find(p => p.id === profileId)
    if (profile) { profile.permissions[permKey] = !profile.permissions[permKey]; save() }
  }

  function setAllPagePerms(profileId: string, pageKey: string, value: boolean) {
    const profile = state.profiles.find(p => p.id === profileId)
    if (!profile) return
    const page = PAGES_CONFIG.find(p => p.key === pageKey)
    if (!page) return
    page.actions.forEach(a => { profile.permissions[`${pageKey}.${a.key}`] = value })
    save()
  }

  function hasPermission(userId: number, teamIds: number[], permKey: string): boolean {
    return resolveUserProfiles(userId, teamIds).some(p => Boolean(p.permissions[permKey]))
  }

  /**
   * Times que o usuário pode escolher pra reatribuir uma conversa. Sem restrição
   * pra ninguém de propósito — qualquer agente pode mover uma conversa pra
   * qualquer time cadastrado, independente de ser membro dele no Chatwoot ou do
   * próprio perfil (antes só Administrador/EC - ISBET viam todos os times aqui).
   * Não confundir com `resolveRoleScope(...).unrestricted`, que continua
   * controlando visibilidade de conversas/agentes — essa função não mexe nisso.
   */
  function getProfileTeamIds(): number[] | null {
    return null
  }

  function canGenerateProtocol(userId: number, teamIds: number[]): boolean {
    if (!state.globalSettings.generateProtocol) return false
    return hasPermission(userId, teamIds, 'messages.generateProtocol')
  }

  function updateGlobalSettings(s: Partial<AdminConfig['globalSettings']>) {
    Object.assign(state.globalSettings, s)
    save()
  }

  function permCount(profile: Profile): { granted: number; total: number } {
    let total = 0
    let granted = 0
    for (const page of PAGES_CONFIG) {
      if (page.exclusive) {
        total += 1
        if (page.actions.some(a => profile.permissions[`${page.key}.${a.key}`])) granted += 1
      } else {
        total += page.actions.length
        granted += page.actions.filter(a => profile.permissions[`${page.key}.${a.key}`]).length
      }
    }
    return { granted, total }
  }

  return {
    profiles:       computed(() => [...state.profiles].sort((a, b) => profileSortIndex(a) - profileSortIndex(b))),
    globalSettings: computed(() => state.globalSettings),
    PAGES_CONFIG,
    addProfile,
    updateProfile,
    deleteProfile,
    togglePermission,
    setAllPagePerms,
    hasPermission,
    getProfileTeamIds,
    canGenerateProtocol,
    updateGlobalSettings,
    permCount,
  }
}
